import { PublicClientApplication, BrowserCacheLocation, LogLevel, } from '@azure/msal-browser';
/**
 * Creates and initialises a PublicClientApplication along with the standard
 * scope request objects used throughout JM Test Atlas.
 *
 * Calling this once at module level is the intended pattern:
 * ```ts
 * export const { msalInstance, loginRequest, apiRequest, graphRequest,
 *                acquireToken, acquireGraphToken, reauthorize } =
 *   createMsalClient({ clientId, tenantId });
 * ```
 *
 * Any other application that targets the same Entra ID tenant can call this
 * factory with its own client ID to get an isolated MSAL instance.
 */
export async function createMsalClient(options) {
    const { clientId, tenantId, redirectUri, apiScope = `api://${clientId}/access_as_user`, verbose = false, } = options;
    const msalConfig = {
        auth: {
            clientId,
            authority: `https://login.microsoftonline.com/${tenantId}`,
            redirectUri: redirectUri ?? (typeof window !== 'undefined' ? window.location.origin : '/'),
            postLogoutRedirectUri: typeof window !== 'undefined' ? window.location.origin : '/',
            navigateToLoginRequestUrl: true,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: false,
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii)
                        return;
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            break;
                        case LogLevel.Warning:
                            console.warn(message);
                            break;
                        case LogLevel.Info:
                            if (verbose)
                                console.info(message);
                            break;
                        case LogLevel.Verbose:
                            if (verbose)
                                console.debug(message);
                            break;
                    }
                },
                logLevel: verbose ? LogLevel.Info : LogLevel.Warning,
                piiLoggingEnabled: false,
            },
        },
    };
    const msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    // Restore active account from cache on page load
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0] ?? null);
    }
    // ── Scope request templates ────────────────────────────────────────────────
    const loginRequest = {
        scopes: ['openid', 'profile', 'email', 'User.Read', apiScope],
    };
    const apiRequest = {
        scopes: [apiScope],
        account: undefined,
    };
    const graphRequest = {
        scopes: ['User.Read', 'User.Read.All'],
        account: undefined,
    };
    // ── Token helpers ──────────────────────────────────────────────────────────
    /**
     * Acquires an access token for the backend API (silent only).
     * Throws if no active account or if the cached token is expired.
     */
    async function acquireToken() {
        const account = msalInstance.getActiveAccount();
        if (!account)
            throw new Error('No active account. User must be signed in.');
        const response = await msalInstance.acquireTokenSilent({ ...apiRequest, account });
        return response.accessToken;
    }
    /**
     * Acquires a Microsoft Graph-scoped access token (silent only).
     * Throws with code `interaction_required` if silent fails — in that case
     * call `reauthorize()` from a user-interaction handler.
     */
    async function acquireGraphTokenSilent() {
        const account = msalInstance.getActiveAccount();
        if (!account)
            throw new Error('No active account. User must be signed in.');
        const response = await msalInstance.acquireTokenSilent({ ...graphRequest, account });
        return response.accessToken;
    }
    /**
     * Acquires a Microsoft Graph-scoped access token.
     * Attempts silent first; falls back to a popup (must be called from a user
     * interaction such as a button click).
     */
    async function acquireGraphToken() {
        const account = msalInstance.getActiveAccount();
        if (!account)
            throw new Error('No active account. User must be signed in.');
        try {
            return (await msalInstance.acquireTokenSilent({ ...graphRequest, account })).accessToken;
        }
        catch {
            return (await msalInstance.acquireTokenPopup({ ...graphRequest, account })).accessToken;
        }
    }
    /**
     * Re-authenticates via loginPopup requesting all scopes (API + Graph).
     * Must be called from a user interaction context (e.g. a button click).
     * After this completes, both `acquireToken` and `acquireGraphToken` will
     * succeed silently because consent is recorded in the Entra ID session.
     */
    async function reauthorize() {
        const account = msalInstance.getActiveAccount();
        // Step 1: loginPopup for API scopes — records consent for the custom resource
        const response = await msalInstance.loginPopup({
            ...loginRequest,
            account: account ?? undefined,
        });
        msalInstance.setActiveAccount(response.account);
        // Step 2: separate acquireTokenPopup for Graph (different resource)
        await msalInstance.acquireTokenPopup({ ...graphRequest, account: response.account });
    }
    return {
        msalInstance,
        loginRequest,
        apiRequest,
        graphRequest,
        acquireToken,
        acquireGraphTokenSilent,
        acquireGraphToken,
        reauthorize,
    };
}
//# sourceMappingURL=index.js.map